import { SelectInput, styled } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';

import { useGoogleFonts } from './hooks/useGoogleFonts';

const Container = styled.div`
  width: 180px;

  > div {
    width: 100%;
  }
`;

interface Props {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  fontFamily: string;
}

export const fontWeightOptions = [
  /* Keyword values */
  'normal',
  'bold',

  /* Keyword values relative to the parent */
  'lighter',
  'bolder',

  /* Numeric keyword values */
  '100',
  '200',
  '300',
  '400', // normal
  '500',
  '600',
  '700', // bold
  '800',
  '900',
];

const globalValues = ['inherit', 'initial', 'unset'];

export const FontWeightPicker = ({ value = '', onChange, disabled, fontFamily }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  // if they're using a google font then we should restrict weight options to the known supported ones
  const googleFonts = useGoogleFonts();

  const match = useMemo(() => {
    return googleFonts.find((font) => font.family === fontFamily);
  }, [fontFamily]);

  const availableOptions = match?.variants
    ? fontWeightOptions.filter((option) => (match.variants as string[]).includes(option))
    : fontWeightOptions;

  const items = [...availableOptions, ...globalValues].map((v) => ({ label: v, value: v }));

  const onClearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchValue) {
      return items;
    }

    return items.filter((item) => {
      return item.label.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, items]);

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  return (
    <Container>
      <SelectInput
        searchable
        clearable={false}
        onFilter={updateSearchValue}
        onOpeningChange={onClearSearch}
        onClearFilter={onClearSearch}
        searchInputProps={{
          placeholder: `Search a font weight`,
        }}
        disabled={disabled}
        selectedItem={items.find((item) => item.value === value)}
        items={filteredItems}
        onSelect={(selected) => {
          if (selected) {
            onChange(selected.value);
          }
        }}
      />
    </Container>
  );
};
